/**
 * Collection and search results: filters, sort, load more, filter drawer below 1100 px.
 * Uses the Section Rendering API so only the results section is re-rendered.
 * Without JavaScript, filters fall back to a normal form submit and "Load more" to page links.
 */
(() => {
  /* Dual-handle price slider ------------------------------------------- */
  class PriceRange extends HTMLElement {
    connectedCallback() {
      this.min = this.querySelector('.price-range__input--min');
      this.max = this.querySelector('.price-range__input--max');
      this.minLabel = this.querySelector('[data-price-min-label]');
      this.maxLabel = this.querySelector('[data-price-max-label]');
      if (!this.min || !this.max) return;
      for (const input of [this.min, this.max]) {
        input.addEventListener('input', () => this.update(input));
      }
      this.update();
    }

    /** @param {number} value */
    format(value) {
      const decimals = 3;
      return `${Number(value).toFixed(decimals)} ${this.dataset.currency || ''}`.trim();
    }

    /** @param {HTMLInputElement} [changed] */
    update(changed) {
      let lo = Number(this.min.value);
      let hi = Number(this.max.value);
      if (lo > hi) {
        if (changed === this.min) { this.min.value = String(hi); lo = hi; } else { this.max.value = String(lo); hi = lo; }
      }
      const top = Number(this.dataset.max) || 1;
      this.style.setProperty('--range-start', `${(lo / top) * 100}%`);
      this.style.setProperty('--range-end', `${100 - (hi / top) * 100}%`);
      if (this.minLabel) this.minLabel.textContent = this.format(lo);
      if (this.maxLabel) this.maxLabel.textContent = this.format(hi);
      this.min.setAttribute('aria-valuetext', this.format(lo));
      this.max.setAttribute('aria-valuetext', this.format(hi));
    }

    /** True when both handles sit at the ends (no price filter) */
    get isDefault() {
      return Number(this.min.value) <= Number(this.min.dataset.default) && Number(this.max.value) >= Number(this.max.dataset.default);
    }
  }
  if (!customElements.get('price-range')) customElements.define('price-range', PriceRange);

  /* Results ------------------------------------------------------------- */
  class CollectionResults extends HTMLElement {
    #controller = null;
    #timer = null;

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (event) => this.#onChange(event));
      this.addEventListener('click', (event) => this.#onClick(event));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panel?.classList.contains('is-open')) this.#closePanel();
      });
      window.addEventListener('popstate', () => this.#render(window.location.href, { push: false }));
    }

    get panel() {
      return this.querySelector('[data-facets-panel]');
    }

    get form() {
      return this.querySelector('[data-facets-form]');
    }

    /** @param {Event} event */
    #onChange(event) {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (!target.form?.matches('[data-facets-form]') && !target.matches('[data-sort]')) return;
      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => this.#render(this.#urlFromForm()), target.type === 'range' ? 500 : 250);
    }

    /** @param {MouseEvent} event */
    #onClick(event) {
      const target = /** @type {Element} */ (event.target);

      const more = target.closest('[data-facet-more]');
      if (more) {
        const facet = more.closest('.facet');
        const expanded = facet.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', String(expanded));
        return;
      }

      const remove = target.closest('[data-facet-remove], [data-facets-clear]');
      if (remove instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.#render(remove.href);
        return;
      }

      const loadMore = target.closest('[data-load-more]');
      if (loadMore instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.#loadMore(loadMore);
        return;
      }

      if (target.closest('[data-facets-open]')) {
        this.#openPanel();
        return;
      }

      if (target.closest('[data-facets-close]')) {
        this.#closePanel();
      }
    }

    /** @returns {string} */
    #urlFromForm() {
      const form = this.form;
      const params = new URLSearchParams();
      if (form) {
        const range = form.querySelector('price-range');
        for (const [key, value] of new FormData(form)) {
          if (range && range.isDefault && key.startsWith('filter.v.price')) continue;
          if (value === '') continue;
          params.append(key, String(value));
        }
      } else {
        const sort = this.querySelector('[data-sort]');
        if (sort) params.set('sort_by', sort.value);
      }
      const url = new URL(window.location.pathname, window.location.origin);
      url.search = params.toString();
      return url.toString();
    }

    /**
     * Fetch the section for a URL and swap in the results.
     * @param {string} href
     * @param {{push?: boolean}} [options]
     */
    async #render(href, { push = true } = {}) {
      this.#controller?.abort();
      this.#controller = new AbortController();
      const main = this.querySelector('.results__main');
      main?.setAttribute('aria-busy', 'true');

      const url = new URL(href, window.location.origin);
      url.searchParams.delete('page');
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(fetchUrl, { signal: this.#controller.signal });
        if (!response.ok) throw new Error(String(response.status));
        const html = await response.text();
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-results-root]');
        const current = this.querySelector('[data-results-root]');
        if (!fresh || !current) throw new Error('Missing results');

        // keep the drawer open, and keep open/closed state of each filter group
        const wasOpen = this.panel?.classList.contains('is-open');
        const openStates = [...current.querySelectorAll('.facet')].map((d) => d.open);
        const expanded = [...current.querySelectorAll('.facet')].map((d) => d.classList.contains('is-expanded'));
        const focusedId = document.activeElement?.id;

        current.replaceWith(fresh);

        [...fresh.querySelectorAll('.facet')].forEach((d, i) => {
          if (openStates[i] !== undefined) d.open = openStates[i];
          if (expanded[i]) {
            d.classList.add('is-expanded');
            d.querySelector('[data-facet-more]')?.setAttribute('aria-expanded', 'true');
          }
        });
        if (wasOpen) this.#openPanel({ focus: false });
        if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

        if (push) history.pushState({}, '', url.toString());
        window.FBC?.observeReveal?.(fresh);
        window.FBC?.wishlist?.sync?.();
      } catch (error) {
        if (error.name !== 'AbortError') window.location.href = url.toString();
      } finally {
        this.querySelector('.results__main')?.removeAttribute('aria-busy');
      }
    }

    /** @param {HTMLAnchorElement} link */
    async #loadMore(link) {
      link.classList.add('is-loading');
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = this.querySelector('[data-results-grid]');
        const newItems = doc.querySelectorAll('[data-results-grid] > li');
        const firstNew = newItems[0];
        for (const item of newItems) grid?.append(item);
        const freshWrap = doc.querySelector('[data-load-more-wrap]');
        const wrap = this.querySelector('[data-load-more-wrap]');
        if (freshWrap && wrap) wrap.replaceWith(freshWrap);
        const count = doc.querySelector('.results__count');
        const currentCount = this.querySelector('.results__count');
        if (count && currentCount) currentCount.textContent = count.textContent;
        window.FBC?.observeReveal?.(grid || this);
        window.FBC?.wishlist?.sync?.();
        // move focus to the first new product for keyboard users
        firstNew?.querySelector('a')?.focus({ preventScroll: true });
      } catch {
        window.location.href = link.href;
      }
    }

    #openPanel({ focus = true } = {}) {
      const panel = this.panel;
      if (!panel) return;
      this.lastTrigger = this.querySelector('[data-facets-open]');
      panel.classList.add('is-open');
      this.querySelector('.results__overlay')?.removeAttribute('hidden');
      this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
      if (window.matchMedia('(max-width: 1099px)').matches) {
        document.body.classList.add('is-locked');
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        if (focus) panel.querySelector('button, input, a')?.focus();
        this.trap = (event) => {
          if (event.key !== 'Tab') return;
          const items = [...panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select')].filter((el) => el.offsetParent !== null);
          if (!items.length) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        };
        panel.addEventListener('keydown', this.trap);
      }
    }

    #closePanel() {
      const panel = this.panel;
      if (!panel) return;
      panel.classList.remove('is-open');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      if (this.trap) panel.removeEventListener('keydown', this.trap);
      this.querySelector('.results__overlay')?.setAttribute('hidden', '');
      const toggle = this.querySelector('[data-facets-open]');
      toggle?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('is-locked');
      toggle?.focus();
    }
  }

  if (!customElements.get('collection-results')) customElements.define('collection-results', CollectionResults);
})();
